/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState, useEffect } from 'react';
import { Box, Heading } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { ButtonCustom, CheckboxCustom, InputCustom } from '../UI';
import AuthLinks from '../authBottomLinks';
import { validationSchema } from './validation';
import { AnimatePresence, motion } from 'framer-motion';
import { SignUpFormProps } from '../../../modules/auth/types';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { signUpAction, sendCodeAction } from '../../../modules/auth/actions';
import { useAuthErrorHandling } from '../../../utils/useAuthErrorHandling';
import { selectIsSignUpErrorAuth } from '../../../modules/auth/selectors';

interface SignUpFormData extends SignUpFormProps {
  rememberMe: boolean;
  agreePrivacyPolicy: boolean;
  newsletter: boolean;
}

const link1 = '/signIn';
const link2 = '/signIn';
const linkName1 = 'Уже есть аккаунт?';
const linkName2 = 'Войти';

const initialValues: SignUpFormData = {
  firstName: '',
  lastName: '',
  phone: '',
  confirmationCode: '',
  password: '',
  confirmPassword: '',
  rememberMe: false,
  agreePrivacyPolicy: false,
  newsletter: false,
};

const SignUpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const isError = useAppSelector(selectIsSignUpErrorAuth);
  const [codeRequested, setCodeRequested] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // Timer set for 60 seconds

  useAuthErrorHandling(isError);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isTimerActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prevTime => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      setTimeLeft(60); // Reset the timer after it reaches 0
    }

    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft]);

  const handleSubmit = useCallback((values: SignUpFormData) => {
    dispatch(signUpAction.request(values));
  }, []);

  const handleRequestCode = useCallback((phone: string) => {
    dispatch(sendCodeAction.request(phone));
    setCodeRequested(true);
    setIsTimerActive(true); // Start the timer when the code is requested
  }, []);

  const formatPhoneNumber = (phone: string) => {
    // Format phone number to include "+"
    const formattedPhone = phone.replace(/[^0-9]/g, ''); // Remove non-numeric characters
    return formattedPhone.length > 0 && !formattedPhone.startsWith('+')
      ? '+' + formattedPhone
      : formattedPhone;
  };
  const phoneValidation = (phone: string) => {
    const phoneRegex = /^[+7|+8]\d{11}$/; // Supports +7 and +8 followed by 11 digits
    return phoneRegex.test(phone);
  };

  const phoneChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, handleChange: any) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    handleChange(e); // Trigger Formik's handleChange
    e.target.value = formattedPhone; // Update input value with formatted phone
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, errors, touched }) => {
          const isPhoneValid = phoneValidation(values.phone) && !errors.phone;
          return (
            <Form style={{ width: '100%', maxWidth: '416px' }}>
              <Heading fontWeight="bold" mb="32px">
                Зарегистрироваться
              </Heading>
              <InputCustom
                label="Имя"
                placeholder="Петр"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                errorMessage={touched.firstName && errors.firstName}
              />
              <InputCustom
                label="Фамилия"
                placeholder="Иванов"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                errorMessage={touched.lastName && errors.lastName}
              />
              <Box display="flex" alignItems="center" position="relative" mt="20px" mb="20px">
                <InputCustom
                  w="189px"
                  label="Телефон"
                  placeholder="+7 999 99-99-99"
                  name="phone"
                  value={values.phone}
                  onChange={e => phoneChangeHandler(e, handleChange)}
                  errorMessage={touched.phone && errors.phone}
                  style={{ borderColor: touched.phone && errors.phone ? 'red' : 'inherit' }} // Red border on invalid phone
                />
                <ButtonCustom
                  position={'absolute'}
                  width="50%"
                  top={0}
                  bottom={0}
                  right={0}
                  mt={'32px'}
                  variant="withBackground"
                  disabled={!isPhoneValid || isTimerActive}
                  onClick={() => handleRequestCode(values.phone)}
                >
                  {isTimerActive ? `Отправить через ${timeLeft}s` : 'Получить код'}
                </ButtonCustom>
              </Box>
              <AnimatePresence>
                {(codeRequested || (touched.phone && !errors.phone)) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box mb="20px">
                      <InputCustom
                        label="Код подтверждения"
                        name="confirmationCode"
                        value={values.confirmationCode}
                        onChange={handleChange}
                        errorMessage={touched.confirmationCode && errors.confirmationCode}
                      />
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
              <InputCustom
                label="Пароль"
                type="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                errorMessage={touched.password && errors.password}
              />
              <Box mt="20px" mb="40px">
                <InputCustom
                  label="Повторите пароль"
                  type="password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  errorMessage={touched.confirmPassword && errors.confirmPassword}
                />
              </Box>

              <ButtonCustom type="submit" isLoading={false} w="100%" variant="withBackground">
                Зарегистрироваться
              </ButtonCustom>

              <CheckboxCustom
                mt="4"
                name="rememberMe"
                label="Запомнить меня"
                isChecked={values.rememberMe}
                onChange={handleChange}
              />

              <CheckboxCustom
                mt="4"
                colorScheme="blue"
                isChecked={values.agreePrivacyPolicy}
                name="agreePrivacyPolicy"
                error={!!(touched.agreePrivacyPolicy && errors.agreePrivacyPolicy)}
                onChange={handleChange}
                label="Нажимая кнопку «Получить код», Вы соглашаетесь с условиями"
                labelWithLink={{
                  link: '#',
                  text: 'политики конфиденциальности',
                }}
              />

              <CheckboxCustom
                label="Согласен получать рассылки и уведомления от магазина"
                mt="2"
                colorScheme="blue"
                isChecked={values.newsletter}
                name="newsletter"
                onChange={handleChange}
              />
            </Form>
          );
        }}
      </Formik>
      <AuthLinks link1Name={linkName1} link1Url={link1} link2Name={linkName2} link2Url={link2} />
    </Box>
  );
};

export default React.memo(SignUpForm);
